/**
 * The year × period coverage matrix.
 *
 * This is the point of the company card. It answers "what do I actually have
 * for this company" in one glance, and a filled cell goes straight to that
 * document's viewer -- skipping the company page entirely when you already
 * know which filing you want (§10.2).
 */
import { Link } from 'wouter'
import type { CoverageYear } from '@server/sql/companies.ts'
import { useT } from '~/i18n/index.tsx'
import { cn } from '~/lib/cn.ts'

/** Fiscal reading order, not alphabetical. */
const ROWS = ['q1', 'q2', 'q3', 'q4', 'annual'] as const

export function CoverageMatrix({
  symbol, coverage, size = 'sm',
}: { symbol: string; coverage: CoverageYear[]; size?: 'sm' | 'md' }) {
  const { v, t } = useT()
  if (coverage.length === 0) return null

  const years = coverage.map((c) => c.year)
  const byYear = new Map(coverage.map((c) => [c.year, c.periods]))

  // A row is dropped when no year has that period at all -- a corpus with no
  // Q4 filings should not carry an empty Q4 row on every card.
  const rows = ROWS.filter((p) => coverage.some((c) => c.periods[p] !== undefined))

  // Wide enough that four-digit years do not run together in the header --
  // and, on a coarse pointer, tall enough to be hit. The *dot* stays small
  // either way: the target grows, the graphic does not, because a matrix of
  // fat dots stops reading as a matrix.
  const cell =
    size === 'md'
      ? 'h-[max(1.75rem,var(--tap-row))] w-12'
      : 'h-[max(22px,var(--tap-row))] w-[max(2.25rem,var(--tap-row))]'
  // The dot grows with the row, but by less: the matrix reads as a matrix
  // because the ink is sparse, and fattening the dots as much as the cells
  // would turn a grid of marks into a grid of blobs.
  const dot = size === 'md' ? 10 : 8

  return (
    <table className="border-separate border-spacing-0 text-[length:var(--text-micro)]">
      <thead>
        <tr>
          <th className="pe-2" />
          {years.map((y) => (
            <th
              key={y}
              className={cn('tnum pb-1 text-center font-normal text-[var(--text-faint)]', cell)}
            >
              {y}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((p) => (
          <tr key={p}>
            <th
              scope="row"
              className="pe-2 text-end font-normal text-[var(--text-faint)] whitespace-nowrap"
            >
              {v('periodShort', p) ?? p}
            </th>
            {years.map((y) => {
              const id = byYear.get(y)?.[p]
              const label = `${v('period', p) ?? p} ${y}`
              return (
                <td key={y} className={cn('relative p-0 text-center align-middle', cell)}>
                  {id === undefined ? (
                    /* An absence. The row and column headers already say
                       which period it is, so this stays out of the
                       accessibility tree rather than announcing "no Q3 2024"
                       twenty-five times per card. */
                    <span
                      aria-hidden="true"
                      title={`${label} — ${t('common.none')}`}
                      className="inline-block rounded-full border border-[var(--border-strong)]"
                      style={{ width: dot, height: dot }}
                    />
                  ) : (
                    /*
                     * The link fills the whole cell rather than being the dot
                     * (§10.2). This is the fastest path in the app -- a filled
                     * cell goes straight to that filing's viewer, skipping the
                     * company page -- and it used to be a 7px circle.
                     *
                     * The dot inside is the only thing painted, so the matrix
                     * still reads as a matrix.
                     */
                    <Link
                      href={`/c/${symbol}/d/${id}`}
                      title={label}
                      aria-label={label}
                      className={cn(
                        'group absolute inset-0 flex items-center justify-center',
                        'rounded-[var(--radius-control)] transition-colors duration-75',
                        'hover:bg-[var(--accent-soft)] active:bg-[var(--accent-soft)]',
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={cn(
                          'block rounded-full bg-[var(--accent)] transition-transform duration-75',
                          'group-hover:scale-[1.45] group-focus-visible:scale-[1.45]',
                          'motion-reduce:group-hover:scale-100',
                        )}
                        style={{ width: dot, height: dot }}
                      />
                    </Link>
                  )}
                </td>
              )
            })}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
